import { ApiError } from '../api/errors';
import type { HttpClient } from '../api/http';
import {
    bootstrapSchema,
    mediaResponseSchema,
    pullSchema,
    pushResponseSchema,
    type BootstrapData,
    type PullData,
    type PushRequest,
    type PushResponse,
} from './wire';

/**
 * De verzoeken van de sync-engine, en alleen die. De http-client komt van
 * buiten (`SyncProvider` geeft `api` mee), zodat tests er een client met een
 * eigen `fetch` in kunnen steken.
 *
 * Voor elk verzoek geldt:
 * - `cacheKey: false` - het antwoord hoort in de database van de omgeving, niet
 *   in de apparaatbrede GET-cache (en een gecachete pull zou offline eindeloos
 *   dezelfde pagina opleveren);
 * - `retryAttempts: 1` - opnieuw proberen en backoff doet de sync-engine zelf.
 */
export interface SyncApi {
    bootstrap(options?: SyncRequestOptions): Promise<BootstrapData>;
    pull(cursor: string, options?: SyncRequestOptions): Promise<PullData>;
    push(request: PushRequest, options?: SyncRequestOptions): Promise<PushResponse>;
    fetchPackage(input: PackageRequest, options?: SyncRequestOptions): Promise<PackageResponse>;
    uploadMedia(input: MediaUpload, options?: SyncRequestOptions): Promise<MediaUploadResult>;
    /** `DELETE /media/{uuid}`. Een foto die er al niet meer is, geldt als verwijderd. */
    deleteMedia(mediaUuid: string, options?: SyncRequestOptions): Promise<void>;
}

/**
 * Het signaal van de sync-ronde: "Nu versturen" breekt een ronde die vastzit af,
 * zodat de volgende poging niet achter een verzoek hoeft te wachten dat nooit
 * meer antwoordt.
 */
export interface SyncRequestOptions {
    signal?: AbortSignal;
}

/**
 * Hoe lang een foto over de lijn mag doen: een halve minuut, plus een seconde
 * per 50 kB - een trage 3G-verbinding in een kruipruimte haalt dat nog. Een
 * vaste drie minuten liet een vastgelopen upload na het openklappen van een
 * laptop drie minuten lang alles ophouden; daarna pas kwam de volgende poging.
 */
export function uploadTimeoutMs(byteSize: number): number {
    return Math.min(180_000, 30_000 + Math.ceil(Math.max(0, byteSize) / 50_000) * 1_000);
}

export interface PackageRequest {
    uuid: string;
    packageUrl: string;
    /** De ETag van het document dat al lokaal staat; levert dan `304`. */
    etag: string | null;
}

export type PackageResponse =
    { status: 'modified'; document: unknown; byteSize: number } | { status: 'not-modified' };

export interface MediaUpload {
    clientUuid: string;
    file: Blob;
    fileName: string;
    /** Een vraagfoto hoort bij een antwoord, een foto van een handmatige bevinding bij die bevinding. */
    mediableType: 'inspection_answer' | 'finding';
    mediableUuid: string;
    inspectionUuid: string;
    caption: string | null;
    takenAt: string | null;
}

export interface MediaUploadResult {
    urls: { thumb: string | null; web: string | null } | null;
    /** Het `id` van de mediaresource. */
    mediaUuid: string | null;
}

export interface SyncApiOptions {
    /** Basis-URL van de client (`…/api/v1`), om een absolute `package_url` terug te brengen tot een pad. */
    baseUrl: string;
    packageTimeoutMs?: number;
}

export function createSyncApi(client: HttpClient, options: SyncApiOptions): SyncApi {
    const base = options.baseUrl.replace(/\/+$/, '');

    return {
        async bootstrap(request) {
            const body = await client.get('/sync/bootstrap', {
                ...signalOf(request),
                cacheKey: false,
                retryAttempts: 1,
                timeoutMs: 60_000,
            });

            return bootstrapSchema.parse(body).data;
        },

        async pull(cursor, request) {
            const body = await client.get('/sync/pull', {
                ...signalOf(request),
                query: { cursor },
                cacheKey: false,
                retryAttempts: 1,
                timeoutMs: 60_000,
            });

            return pullSchema.parse(body).data;
        },

        async push(request, options) {
            const body = await client.post('/sync/push', request, {
                ...signalOf(options),
                idempotencyKey: request.batch_uuid,
                retryAttempts: 1,
                timeoutMs: 60_000,
            });

            return pushResponseSchema.parse(body).data;
        },

        async fetchPackage({ uuid, packageUrl, etag }, request) {
            try {
                const document = await client.get(packagePath(base, packageUrl, uuid), {
                    ...signalOf(request),
                    cacheKey: false,
                    retryAttempts: 1,
                    timeoutMs: options.packageTimeoutMs ?? 180_000,
                    ...(etag ? { headers: { 'If-None-Match': etag } } : {}),
                });

                return {
                    status: 'modified',
                    document,
                    byteSize: (JSON.stringify(document) ?? '').length,
                };
            } catch (error) {
                if (error instanceof ApiError && error.status === 304) {
                    return { status: 'not-modified' };
                }

                throw error;
            }
        },

        async uploadMedia(input, request) {
            const form = new FormData();
            form.append('file', input.file, input.fileName);
            form.append('client_uuid', input.clientUuid);
            form.append('collection', 'question_photo');
            form.append('mediable_type', input.mediableType);
            form.append('mediable_uuid', input.mediableUuid);
            form.append('inspection_uuid', input.inspectionUuid);

            if (input.caption) {
                form.append('caption', input.caption);
            }

            if (input.takenAt) {
                form.append('taken_at', input.takenAt);
            }

            const body = await client.post('/media', form, {
                ...signalOf(request),
                idempotencyKey: input.clientUuid,
                retryAttempts: 1,
                timeoutMs: uploadTimeoutMs(input.file.size),
            });
            const media = mediaResponseSchema.safeParse(body).data?.data;
            const urls = media?.urls;

            return {
                urls: urls
                    ? {
                          thumb: urls.thumb ?? urls.original ?? null,
                          web: urls.web ?? urls.original ?? null,
                      }
                    : null,
                mediaUuid: media?.id ?? media?.uuid ?? null,
            };
        },

        async deleteMedia(mediaUuid, request) {
            try {
                await client.delete(`/media/${mediaUuid}`, {
                    ...signalOf(request),
                    idempotencyKey: mediaUuid,
                    retryAttempts: 1,
                    expect: 'none',
                });
            } catch (error) {
                if (error instanceof ApiError && error.status === 404) {
                    return;
                }

                throw error;
            }
        },
    };
}

function signalOf(options: SyncRequestOptions | undefined): { signal?: AbortSignal } {
    return options?.signal ? { signal: options.signal } : {};
}

/**
 * `package_url` is absoluut. De http-client plakt zelf de basis-URL ervoor, dus
 * hier wordt het pad eruit gehaald; wijst hij ergens anders heen, dan is het
 * pad uit het contract de terugval.
 */
export function packagePath(baseUrl: string, packageUrl: string, uuid: string): string {
    if (packageUrl.startsWith(baseUrl)) {
        return packageUrl.slice(baseUrl.length) || '/';
    }

    try {
        const base = new URL(baseUrl);
        const url = new URL(packageUrl, base);
        const basePath = base.pathname.replace(/\/+$/, '');

        if (url.origin === base.origin && url.pathname.startsWith(`${basePath}/`)) {
            return url.pathname.slice(basePath.length) + url.search;
        }
    } catch {
        // Geen geldige URL: val terug op het pad uit het contract.
    }

    return `/template-versions/${uuid}/package`;
}

/** `true` voor een probleem van dit type (`…/problems/<naam>`). */
export function isProblem(error: unknown, name: string): boolean {
    return (
        error instanceof ApiError &&
        typeof error.problem.type === 'string' &&
        error.problem.type.endsWith(`/problems/${name}`)
    );
}
